/**
 * Platform ops capability matrix (ops < admin < platform).
 */
import type { PlatformOpsRole } from "./platform-ops";

export const PLATFORM_CAPABILITIES = Object.freeze([
  "platform.dashboard.read",
  "platform.requests.read",
  "platform.requests.rematch",
  "platform.requests.note",
  "platform.partnerships.read",
  "platform.partnerships.write",
  "platform.businesses.read",
  "platform.businesses.write",
  "platform.users.read",
  "platform.users.write",
  "platform.customers.read",
  "platform.business_members.read",
  "platform.business_members.write",
  "platform.team.read",
  "platform.team.write",
  "platform.fleet.read",
  "platform.audit.read",
  "platform.accounts.manage",
  "platform.notifications.manage",
  "platform.messaging.use",
  "platform.security.manage",
] as const);

export type PlatformCapability = (typeof PLATFORM_CAPABILITIES)[number];

const OPS_CAPS = Object.freeze([
  "platform.dashboard.read",
  "platform.requests.read",
  "platform.requests.note",
  "platform.partnerships.read",
  "platform.partnerships.write",
  "platform.businesses.read",
  "platform.users.read",
  "platform.customers.read",
  "platform.business_members.read",
  "platform.fleet.read",
  "platform.messaging.use",
] as const satisfies readonly PlatformCapability[]);

const SUPPORT_CAPS = Object.freeze([
  "platform.dashboard.read",
  "platform.requests.read",
  "platform.requests.note",
  "platform.partnerships.read",
  "platform.customers.read",
  "platform.business_members.read",
  "platform.messaging.use",
] as const satisfies readonly PlatformCapability[]);

const FINANCE_CAPS = Object.freeze([
  "platform.dashboard.read",
  "platform.requests.read",
  "platform.businesses.read",
  "platform.customers.read",
  "platform.business_members.read",
  "platform.messaging.use",
] as const satisfies readonly PlatformCapability[]);

const ADMIN_CAPS = Object.freeze([
  ...OPS_CAPS,
  "platform.requests.rematch",
  "platform.businesses.write",
  "platform.users.write",
  "platform.business_members.write",
  "platform.team.read",
  "platform.audit.read",
  "platform.accounts.manage",
  "platform.notifications.manage",
] as const satisfies readonly PlatformCapability[]);

const PLATFORM_CAPS = Object.freeze([
  ...ADMIN_CAPS,
  "platform.security.manage",
  "platform.team.write",
] as const satisfies readonly PlatformCapability[]);

export function platformCapabilitiesFor(
  role: PlatformOpsRole,
): readonly PlatformCapability[] {
  if (role === "platform") return PLATFORM_CAPS;
  if (role === "admin") return ADMIN_CAPS;
  if (role === "support") return SUPPORT_CAPS;
  if (role === "finance") return FINANCE_CAPS;
  return OPS_CAPS;
}

export function platformRoleAllows(
  role: PlatformOpsRole,
  capability: PlatformCapability,
): boolean {
  return platformCapabilitiesFor(role).includes(capability);
}
